import { EmailOutlined } from "@mui/icons-material";
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { Icon } from "@platform/design-system";
import { useState } from "react";

import { EmailCarrier } from "./emailCarriers";

// TODO: In the incoming tickets map new options here
export type EmailMenuOptionKey =
  | "close-opportunity-missing-data"
  | "compile-questions-to-rfp-submitter"
  | "compile-questions-to-rfp-submitter-rush"
  | "confirm-receipt"
  | "marketing-status-update"
  | "sold-case-follow-up";

export type CarrierEmailOptionKey =
  | "carrier-follow-up"
  | "compile-email-to-carrier";

const CARRIER_MENU_KIND = "carrier-menu";

type EmailMenuOption =
  | { kind: "template"; key: EmailMenuOptionKey; label: string }
  | {
      kind: typeof CARRIER_MENU_KIND;
      key: CarrierEmailOptionKey;
      label: string;
    };

const EMAIL_MENU_OPTIONS: readonly EmailMenuOption[] = [
  {
    kind: "template",
    key: "close-opportunity-missing-data",
    label: "Close Opportunity - Missing Data",
  },
  {
    kind: CARRIER_MENU_KIND,
    key: "carrier-follow-up",
    label: "Carrier Follow Up",
  },
  {
    kind: CARRIER_MENU_KIND,
    key: "compile-email-to-carrier",
    label: "Compile Email to Carrier",
  },
  {
    kind: "template",
    key: "compile-questions-to-rfp-submitter",
    label: "Compile Questions to RFP Submitter",
  },
  {
    kind: "template",
    key: "compile-questions-to-rfp-submitter-rush",
    label: "Compile Questions to RFP Submitter - Rush RFP",
  },
  { kind: "template", key: "confirm-receipt", label: "Confirm receipt" },
  {
    kind: "template",
    key: "marketing-status-update",
    label: "Marketing Status Update",
  },
  {
    kind: "template",
    key: "sold-case-follow-up",
    label: "Sold Case Follow Up",
  },
];

const CARRIER_MENU_WIDTH = 350;

const MENU_BORDER_RADIUS = 2;

const OUTLINED_MENU_PAPER = { variant: "outlined", elevation: 0 } as const;

type CarrierMenu = {
  anchorEl: HTMLElement | null;
  optionKey: CarrierEmailOptionKey;
};

type EmailMenuButtonProps = {
  carriers: readonly EmailCarrier[];
  onSelect: (optionKey: EmailMenuOptionKey) => void;
  onSelectCarrierEmail: (
    optionKey: CarrierEmailOptionKey,
    carrier: EmailCarrier,
  ) => void;
  onAddMissingSalesRepEmail: () => void;
};

export const EmailMenuButton = ({
  carriers,
  onSelect,
  onSelectCarrierEmail,
  onAddMissingSalesRepEmail,
}: EmailMenuButtonProps): React.JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [carrierMenu, setCarrierMenu] = useState<CarrierMenu | null>(null);
  const isOpen = anchorEl !== null;
  const isCarrierMenuOpen = carrierMenu !== null;
  const hasCarrierMissingSalesRep = carriers.some(
    (carrier) => carrier.salesRepEmails.length === 0,
  );

  const closeCarrierMenu = (): void => setCarrierMenu(null);

  const closeMenu = (): void => {
    closeCarrierMenu();
    setAnchorEl(null);
  };

  const handleSelect = (optionKey: EmailMenuOptionKey): void => {
    closeMenu();
    onSelect(optionKey);
  };

  const handleSelectCarrier = (
    optionKey: CarrierEmailOptionKey,
    carrier: EmailCarrier,
  ): void => {
    closeMenu();
    onSelectCarrierEmail(optionKey, carrier);
  };

  const handleAddMissingSalesRepEmail = (): void => {
    closeMenu();
    onAddMissingSalesRepEmail();
  };

  return (
    <>
      <Tooltip title="Send Email">
        <IconButton
          aria-label="Send Email"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          <EmailOutlined />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            ...OUTLINED_MENU_PAPER,
            sx: { mt: 1, borderRadius: MENU_BORDER_RADIUS },
          },
        }}
      >
        {EMAIL_MENU_OPTIONS.map((option) =>
          option.kind === CARRIER_MENU_KIND ? (
            <MenuItem
              key={option.key}
              aria-haspopup="menu"
              aria-expanded={carrierMenu?.optionKey === option.key}
              onClick={(event) =>
                setCarrierMenu({
                  anchorEl: event.currentTarget.parentElement,
                  optionKey: option.key,
                })
              }
              sx={{ justifyContent: "space-between", gap: 2 }}
            >
              {option.label}
              <Icon name="chevronRight" />
            </MenuItem>
          ) : (
            <MenuItem key={option.key} onClick={() => handleSelect(option.key)}>
              {option.label}
            </MenuItem>
          ),
        )}
      </Menu>
      <Menu
        anchorEl={carrierMenu?.anchorEl}
        open={isCarrierMenuOpen}
        onClose={closeCarrierMenu}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            ...OUTLINED_MENU_PAPER,
            sx: {
              width: CARRIER_MENU_WIDTH,
              ml: 2,
              borderRadius: MENU_BORDER_RADIUS,
            },
          },
        }}
      >
        {carriers.length === 0 && (
          <MenuItem disabled>No carriers available</MenuItem>
        )}
        {carriers.map((carrier) => {
          const hasSalesRep = carrier.salesRepEmails.length > 0;

          return (
            <MenuItem
              key={carrier.name}
              disabled={!hasSalesRep}
              onClick={
                hasSalesRep && carrierMenu
                  ? () => handleSelectCarrier(carrierMenu.optionKey, carrier)
                  : undefined
              }
              sx={{ whiteSpace: "normal" }}
            >
              {hasSalesRep
                ? carrier.name
                : `${carrier.name} (Missing Sales Rep Email)`}
            </MenuItem>
          );
        })}
        {hasCarrierMissingSalesRep && <Divider />}
        {hasCarrierMissingSalesRep && (
          <MenuItem onClick={handleAddMissingSalesRepEmail}>
            <ListItemIcon>
              <Icon name="add" />
            </ListItemIcon>
            <ListItemText>Add Missing Sales Rep Email</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
